import React from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import HeroStyle5 from "../Hero/HeroStyle5";
import EngineeringSection from "../EngineeringSection";
import PhilosophySection from "../PhilosophySection";
import { FaBrain } from "react-icons/fa6";
import { pageTitle } from "../../helpers/PageTitle";
import { blogsData } from "../../data/blogsData";

export default function TechStartupPage() {
  pageTitle("Home");
  return (
    <>
      <HeroStyle5
        highlightTitle="End-to-End Digital Engineering"
        title="Your Vision. Engineered to Perfection."
        subTitle="We are a developer-led agency building secure, scalable software across any tech stack. From custom AI/ML integrations to high-performance frontend and backend architecture, we transform your complex requirements into a flawless digital reality"
      />

      {/* Spacer */}
      <div className="h-[95px] max-[991px]:h-[60px]" />

      {/* Engineering Solutions */}
      <EngineeringSection />

      {/* Philosophy */}
      <PhilosophySection />

      {/* Services Grid */}
      <section className="py-12 sm:py-16 lg:py-[80px] relative overflow-hidden bg-gray-50/50">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 relative z-10">
          {/* Header */}
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-10 h-[2px] bg-gradient-to-r from-transparent to-[#1b667a]" />
              <span className="text-[11px] font-bold tracking-[3px] text-[#1b667a] uppercase">
                Core Capabilities
              </span>
              <div className="w-10 h-[2px] bg-gradient-to-l from-transparent to-[#1b667a]" />
            </div>
            <h2
              className="text-[48px] leading-[1.15] font-bold text-gray-900 mb-5 max-w-[700px] mx-auto
              max-[1199px]:text-[38px] max-[991px]:text-[34px] max-[575px]:text-[28px]"
            >
              Engineering High-Performance Solutions for{" "}
              <span className=" text-[#1b667a]">Modern Business</span>
            </h2>
            <p className="text-gray-500 max-w-[520px] mx-auto leading-relaxed text-[15.5px]">
              We combine deep technical expertise with innovative engineering to
              deliver scalable, secure, and future-ready digital solutions.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Card 01 - AI & Machine Learning (Dark teal bg) */}
            <Link
              to="/service/ai-machine-learning"
              className="group relative rounded-2xl overflow-hidden transition-all duration-500 shadow-[0_2px_16px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_40px_rgba(13,79,79,0.25)] hover:-translate-y-2 hover:scale-[1.02] bg-gradient-to-b from-[#0d4f4f] to-[#0a3d3d]"
            >
              {/* Top colored area */}
              <div className="relative px-6 pt-6 pb-20">
                <span className="text-[34px] font-bold text-white/90">01</span>
                {/* Decorative circles */}
                <div className="absolute top-8 right-4 w-[70px] h-[70px] rounded-full border border-emerald-400/20" />
                <div className="absolute top-12 right-8 w-[50px] h-[50px] rounded-full border border-emerald-400/15" />
                {/* Brain icon outline */}
                <div className="absolute top-6 right-6">
                  <div className="w-[56px] h-[56px] rounded-full border-2 border-emerald-400/40 flex items-center justify-center">
                    <FaBrain size={26} className="text-emerald-400" />
                  </div>
                </div>
              </div>
              {/* Content on dark bg */}
              <div className="px-6 pb-6">
                <h3 className="text-[19px] font-bold text-white mb-2 leading-tight">
                  AI & Machine Learning
                </h3>
                <p className="text-[14px] text-emerald-100/50 leading-[1.7] mb-4">
                  Leverage AI, predictive analytics, and intelligent automation
                  to unlock deeper insights and drive operational efficiency.
                </p>
                <div className="flex items-center gap-2 text-emerald-400 text-[14px] font-semibold group-hover:gap-3 transition-all duration-300">
                  Learn More <Icon icon="mdi:arrow-right" className="w-4 h-4" />
                </div>
              </div>
              {/* Bottom wave decoration */}
              <div className="absolute bottom-0 left-0 right-0 h-16 opacity-30">
                <svg viewBox="0 0 300 50" className="w-full h-full" fill="none">
                  <path
                    d="M0,35 Q75,10 150,35 T300,35"
                    stroke="#10B981"
                    strokeWidth="1.5"
                    opacity="0.5"
                  />
                  <path
                    d="M0,42 Q75,17 150,42 T300,42"
                    stroke="#10B981"
                    strokeWidth="1"
                    opacity="0.3"
                  />
                  <path
                    d="M0,48 Q75,23 150,48 T300,48"
                    stroke="#10B981"
                    strokeWidth="0.8"
                    opacity="0.2"
                  />
                </svg>
              </div>
            </Link>

            {/* Card 02 - Web App Development (Light bg) */}
            <Link
              to="/service/web-application-development"
              className="group relative rounded-2xl overflow-hidden transition-all duration-500 shadow-[0_2px_16px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_40px_rgba(59,130,246,0.18)] hover:-translate-y-2 hover:scale-[1.02] bg-white border border-gray-100"
            >
              {/* Top area */}
              <div className="relative px-6 pt-6 pb-20">
                <span className="text-[34px] font-bold text-blue-500">02</span>
                {/* Code icon in circle */}
                <div className="absolute top-6 right-6">
                  <div className="w-[56px] h-[56px] rounded-full border-2 border-blue-200 flex items-center justify-center">
                    <Icon
                      icon="mdi:code-braces"
                      className="w-7 h-7 text-blue-500"
                    />
                  </div>
                </div>
              </div>
              {/* Content */}
              <div className="px-6 pb-6">
                <h3 className="text-[19px] font-bold text-gray-900 mb-2 leading-tight">
                  Web App Development
                </h3>
                <p className="text-[14px] text-gray-400 leading-[1.7] mb-4">
                  Build fast, secure, and scalable web applications using modern
                  frameworks and robust backend architectures.
                </p>
                <div className="flex items-center gap-2 text-blue-500 text-[14px] font-semibold group-hover:gap-3 transition-all duration-300">
                  Learn More <Icon icon="mdi:arrow-right" className="w-4 h-4" />
                </div>
              </div>
              {/* Bottom dots decoration */}
              <div className="absolute bottom-3 right-3 grid grid-cols-5 gap-1.5 opacity-15">
                {[...Array(15)].map((_, i) => (
                  <div
                    key={i}
                    className="w-1.5 h-1.5 rounded-full bg-blue-400"
                  />
                ))}
              </div>
            </Link>

            {/* Card 03 - Mobile Development (Dark purple bg) */}
            <Link
              to="/service/mobile-app-development"
              className="group relative rounded-2xl overflow-hidden transition-all duration-500 shadow-[0_2px_16px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_40px_rgba(76,29,149,0.25)] hover:-translate-y-2 hover:scale-[1.02] bg-gradient-to-b from-[#4c1d95] to-[#3b1578]"
            >
              {/* Top colored area */}
              <div className="relative px-6 pt-6 pb-20">
                <span className="text-[34px] font-bold text-white/90">03</span>
                {/* Phone icon in circle */}
                <div className="absolute top-6 right-6">
                  <div className="w-[56px] h-[56px] rounded-full border-2 border-violet-400/40 flex items-center justify-center">
                    <Icon
                      icon="mdi:cellphone"
                      className="w-7 h-7 text-violet-300"
                    />
                  </div>
                </div>
              </div>
              {/* Content on dark bg */}
              <div className="px-6 pb-6">
                <h3 className="text-[19px] font-bold text-white mb-2 leading-tight">
                  Mobile Development
                </h3>
                <p className="text-[14px] text-violet-100/50 leading-[1.7] mb-4">
                  Deliver intuitive, high-performance mobile experiences that
                  drive engagement, retention, and business growth.
                </p>
                <div className="flex items-center gap-2 text-violet-300 text-[14px] font-semibold group-hover:gap-3 transition-all duration-300">
                  Learn More <Icon icon="mdi:arrow-right" className="w-4 h-4" />
                </div>
              </div>
              {/* Circle decoration */}
              <div className="absolute -bottom-8 -right-8 w-24 h-24 border-2 border-violet-400/15 rounded-full" />
              <div className="absolute -bottom-4 -right-4 w-14 h-14 border border-violet-400/10 rounded-full" />
            </Link>

            {/* Card 04 - Cloud & DevOps (Light bg) */}
            <Link
              to="/service/devops-cloud-solutions"
              className="group relative rounded-2xl overflow-hidden transition-all duration-500 shadow-[0_2px_16px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_40px_rgba(6,182,212,0.18)] hover:-translate-y-2 hover:scale-[1.02] bg-white border border-gray-100"
            >
              {/* Top area */}
              <div className="relative px-6 pt-6 pb-20">
                <span className="text-[34px] font-bold text-teal-500">04</span>
                {/* Cloud icon in circle */}
                <div className="absolute top-6 right-6">
                  <div className="w-[56px] h-[56px] rounded-full border-2 border-teal-200 flex items-center justify-center">
                    <Icon icon="mdi:cloud" className="w-7 h-7 text-teal-500" />
                  </div>
                </div>
              </div>
              {/* Content */}
              <div className="px-6 pb-6">
                <h3 className="text-[19px] font-bold text-gray-900 mb-2 leading-tight">
                  Cloud & DevOps
                </h3>
                <p className="text-[14px] text-gray-400 leading-[1.7] mb-4">
                  Automate, deploy, and scale with modern DevOps practices and
                  cloud-native architectures for maximum reliability.
                </p>
                <div className="flex items-center gap-2 text-teal-500 text-[14px] font-semibold group-hover:gap-3 transition-all duration-300">
                  Learn More <Icon icon="mdi:arrow-right" className="w-4 h-4" />
                </div>
              </div>
              {/* Grid squares decoration */}
              <div className="absolute bottom-3 right-3 grid grid-cols-4 gap-1 opacity-15">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="w-2 h-2 rounded-[2px] bg-teal-400" />
                ))}
              </div>
            </Link>

            {/* Card 05 - Cybersecurity (Light bg) */}
            <Link
              to="/service/cybersecurity-solutions"
              className="group relative rounded-2xl overflow-hidden transition-all duration-500 shadow-[0_2px_16px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_40px_rgba(245,158,11,0.18)] hover:-translate-y-2 hover:scale-[1.02] bg-white border border-gray-100"
            >
              {/* Top area */}
              <div className="relative px-6 pt-6 pb-20">
                <span className="text-[34px] font-bold text-amber-500">05</span>
                {/* Shield icon in circle */}
                <div className="absolute top-6 right-6">
                  <div className="w-[56px] h-[56px] rounded-full border-2 border-amber-200 flex items-center justify-center">
                    <Icon
                      icon="mdi:shield-check"
                      className="w-7 h-7 text-amber-500"
                    />
                  </div>
                </div>
              </div>
              {/* Content */}
              <div className="px-6 pb-6">
                <h3 className="text-[19px] font-bold text-gray-900 mb-2 leading-tight">
                  Cybersecurity Solutions
                </h3>
                <p className="text-[14px] text-gray-400 leading-[1.7] mb-4">
                  Protect your digital assets with robust security frameworks,
                  threat detection, and compliance best practices.
                </p>
                <div className="flex items-center gap-2 text-amber-500 text-[14px] font-semibold group-hover:gap-3 transition-all duration-300">
                  Learn More <Icon icon="mdi:arrow-right" className="w-4 h-4" />
                </div>
              </div>
              {/* Lock decoration */}
              <div className="absolute -bottom-3 -right-3 opacity-15">
                <Icon
                  icon="mdi:lock-outline"
                  className="w-14 h-14 text-amber-400"
                />
              </div>
            </Link>

            {/* Card 06 - Data Engineering (Dark navy bg) */}
            <Link
              to="/service/data-analytics"
              className="group relative rounded-2xl overflow-hidden transition-all duration-500 shadow-[0_2px_16px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_40px_rgba(30,41,59,0.25)] hover:-translate-y-2 hover:scale-[1.02] bg-gradient-to-b from-[#1e293b] to-[#0f172a]"
            >
              {/* Top colored area */}
              <div className="relative px-6 pt-6 pb-20">
                <span className="text-[34px] font-bold text-white/90">06</span>
                {/* Database icon in circle */}
                <div className="absolute top-6 right-6">
                  <div className="w-[56px] h-[56px] rounded-full border-2 border-slate-400/30 flex items-center justify-center">
                    <Icon
                      icon="mdi:database"
                      className="w-7 h-7 text-slate-400"
                    />
                  </div>
                </div>
              </div>
              {/* Content on dark bg */}
              <div className="px-6 pb-6">
                <h3 className="text-[19px] font-bold text-white mb-2 leading-tight">
                  Data Engineering & Analytics
                </h3>
                <p className="text-[14px] text-slate-300/40 leading-[1.7] mb-4">
                  Transform raw data into actionable insights with scalable
                  pipelines, warehousing, and real-time analytics.
                </p>
                <div className="flex items-center gap-2 text-slate-400 text-[14px] font-semibold group-hover:gap-3 transition-all duration-300">
                  Learn More <Icon icon="mdi:arrow-right" className="w-4 h-4" />
                </div>
              </div>
              {/* Bar chart decoration */}
              <div className="absolute bottom-3 right-3 flex items-end gap-1.5 opacity-30">
                <div className="w-2.5 h-5 bg-blue-400 rounded-t" />
                <div className="w-2.5 h-8 bg-blue-500 rounded-t" />
                <div className="w-2.5 h-11 bg-blue-400 rounded-t" />
                <div className="w-2.5 h-7 bg-blue-500 rounded-t" />
                <div className="w-2.5 h-9 bg-blue-400 rounded-t" />
              </div>
            </Link>

            {/* Card 07 - UI/UX Design (Light bg) */}
            <Link
              to="/service/web-application-development"
              className="group relative rounded-2xl overflow-hidden transition-all duration-500 shadow-[0_2px_16px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_40px_rgba(244,63,94,0.18)] hover:-translate-y-2 hover:scale-[1.02] bg-white border border-gray-100"
            >
              {/* Top area */}
              <div className="relative px-6 pt-6 pb-20">
                <span className="text-[34px] font-bold text-rose-500">07</span>
                {/* Layout icon in circle */}
                <div className="absolute top-6 right-6">
                  <div className="w-[56px] h-[56px] rounded-full border-2 border-rose-200 flex items-center justify-center">
                    <Icon
                      icon="mdi:calendar-month-outline"
                      className="w-7 h-7 text-rose-500"
                    />
                  </div>
                </div>
              </div>
              {/* Content */}
              <div className="px-6 pb-6">
                <h3 className="text-[19px] font-bold text-gray-900 mb-2 leading-tight">
                  UI/UX Design & Engineering
                </h3>
                <p className="text-[14px] text-gray-400 leading-[1.7] mb-4">
                  Design beautiful, intuitive, and user-centric interfaces that
                  deliver exceptional digital experiences.
                </p>
                <div className="flex items-center gap-2 text-rose-500 text-[14px] font-semibold group-hover:gap-3 transition-all duration-300">
                  Learn More <Icon icon="mdi:arrow-right" className="w-4 h-4" />
                </div>
              </div>
              {/* Dots decoration */}
              <div className="absolute bottom-3 right-3 grid grid-cols-6 gap-1 opacity-12">
                {[...Array(12)].map((_, i) => (
                  <div key={i} className="w-1 h-1 rounded-full bg-rose-400" />
                ))}
              </div>
            </Link>

            {/* Card 08 - Product Engineering (Dark teal bg) */}
            <Link
              to="/service/digital-transformation-consulting"
              className="group relative rounded-2xl overflow-hidden transition-all duration-500 shadow-[0_2px_16px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_40px_rgba(6,95,95,0.25)] hover:-translate-y-2 hover:scale-[1.02] bg-gradient-to-b from-[#065f5f] to-[#064e4e]"
            >
              {/* Top colored area */}
              <div className="relative px-6 pt-6 pb-20">
                <span className="text-[34px] font-bold text-white/90">08</span>
                {/* Cube icon in circle */}
                <div className="absolute top-6 right-6">
                  <div className="w-[56px] h-[56px] rounded-full border-2 border-teal-400/40 flex items-center justify-center">
                    <Icon
                      icon="mdi:cube-outline"
                      className="w-7 h-7 text-teal-300"
                    />
                  </div>
                </div>
              </div>
              {/* Content on dark bg */}
              <div className="px-6 pb-6">
                <h3 className="text-[19px] font-bold text-white mb-2 leading-tight">
                  Digital Transformation Consulting
                </h3>
                <p className="text-[14px] text-teal-100/40 leading-[1.7] mb-4">
                  End-to-end product development and continuous support to
                  ensure your product evolves and excels.
                </p>
                <div className="flex items-center gap-2 text-teal-300 text-[14px] font-semibold group-hover:gap-3 transition-all duration-300">
                  Learn More <Icon icon="mdi:arrow-right" className="w-4 h-4" />
                </div>
              </div>
              {/* Wave decoration */}
              <div className="absolute bottom-0 left-0 right-0 h-14 opacity-30">
                <svg viewBox="0 0 300 40" className="w-full h-full" fill="none">
                  <path
                    d="M0,25 Q75,5 150,25 T300,25"
                    stroke="#14b8a6"
                    strokeWidth="1.5"
                    opacity="0.6"
                  />
                  <path
                    d="M0,32 Q75,12 150,32 T300,32"
                    stroke="#14b8a6"
                    strokeWidth="1"
                    opacity="0.4"
                  />
                </svg>
              </div>
            </Link>
          </div>

          {/* Bottom CTA */}
          <div className="mt-12 text-center">
            <Link
              to="/service"
              className="inline-flex items-center gap-2.5 bg-[#1b667a] text-white px-8 py-3.5 rounded-[15px] text-[14px] font-semibold hover:bg-[#155568] transition-all duration-400 hover:-translate-y-0.5 shadow-[0_4px_20px_rgba(27,102,122,0.18)] hover:shadow-[0_8px_30px_rgba(27,102,122,0.25)]"
            >
              View All 15 Services
              <Icon icon="mdi:arrow-right" className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Fun Fact */}
      {/* <div className="h-[125px] max-[991px]:h-[70px]" />
      <div className="max-w-[1200px] mx-auto px-6">
        <FunFact data={funfactData} />
      </div>
      <div className="h-[125px] max-[991px]:h-[70px]" />

      {/* Testimonials */}
      {/* <TestimonialSliderStyle2 data={testimonialData} /> */}

      {/* Blog */}
      <section className="py-12 sm:py-16 lg:py-[80px] bg-white overflow-hidden">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
          {/* Header */}
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-10 h-[2px] bg-gradient-to-r from-transparent to-[#1b667a]" />
              <span className="text-[11px] font-bold tracking-[3px] text-[#1b667a] uppercase">
                Insights & News
              </span>
              <div className="w-10 h-[2px] bg-gradient-to-l from-transparent to-[#1b667a]" />
            </div>
            <div className="w-2 h-2 rounded-full bg-[#1b667a] mx-auto mb-5" />
            <h2
              className="text-[44px] leading-[1.15] font-bold text-gray-900 mb-5 max-w-[700px] mx-auto
              max-[1199px]:text-[36px] max-[991px]:text-[30px] max-[575px]:text-[26px]"
            >
              Exploring the world of innovation in our{" "}
              <span className=" text-[#1b667a]">recent news</span>
            </h2>
            <p className="text-gray-500 max-w-[520px] mx-auto leading-relaxed text-[14.5px]">
              Stay updated with our latest insights, trends, and breakthroughs
              from the world of technology and engineering.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {blogsData.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <img
                  src={post.heroImage}
                  alt={post.title}
                  className="h-[190px] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="p-5">
                  <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#1b667a]">
                    {post.category}
                  </p>
                  <h3 className="mb-4 text-[17px] font-bold leading-snug text-gray-900">
                    {post.title}
                  </h3>
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>{post.date}</span>
                    <span>{post.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 lg:py-[80px] bg-gray-50/50 relative overflow-hidden">
        <div className="max-w-[750px] mx-auto px-4 sm:px-6 relative z-10 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-3 mb-5">
            <div className="w-2 h-2 rounded-full " />
            <span className="text-[11px] font-bold tracking-[3px] text-[#1b667a] uppercase">
              Building What Matters
            </span>
            <div className="w-2 h-2 rounded-full bg-[#1b667a]" />
          </div>

          {/* Title */}
          <h2
            className="text-[44px] leading-[1.1] font-bold text-gray-900 mb-4
            max-[1199px]:text-[38px] max-[991px]:text-[30px] max-[575px]:text-[26px]"
          >
            Stop compromising on your digital{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1b667a] via-teal-400 to-green-500">
              infrastructure.
            </span>
          </h2>

          {/* Subtitle */}
          <p className="text-gray-500 max-w-[480px] mx-auto leading-[1.7] text-[14px] mb-7">
            We engineer secure, scalable and high-performance systems that help
            your business grow without limitations.
          </p>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-10 sm:mb-12">
            <Link
              to="/contact"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-brand-teal text-white px-6 py-3 rounded-[12px] text-[13px] font-semibold hover:bg-[#155568] transition-all duration-300 shadow-[0_4px_15px_rgba(27,102,122,0.25)] hover:shadow-[0_6px_20px_rgba(27,102,122,0.35)]"
            >
              Start a Conversation
              <Icon icon="mdi:arrow-top-right" className="w-3.5 h-3.5" />
            </Link>
            <Link
              to="/service"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-gray-600 px-5 py-3 rounded-[12px] text-[13px] font-semibold transition-all duration-300 group border border-gray-200 hover:border-emerald-200 hover:text-[#1b667a] bg-white sm:bg-transparent"
            >
              Explore Services
              <Icon
                icon="mdi:arrow-right"
                className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform"
              />
            </Link>
          </div>

          {/* Bottom Features */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-10 border-t border-gray-200/60">
            <div className="flex flex-col items-center text-center group">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 group-hover:bg-[#1b667a] flex items-center justify-center mb-2.5 transition-colors duration-300">
                <Icon
                  icon="mdi:shield-check"
                  className="w-4.5 h-4.5 text-[#1b667a] group-hover:text-white transition-colors"
                />
              </div>
              <h4 className="text-[13px] font-bold text-gray-900 mb-0.5">
                Secure by Design
              </h4>
              <p className="text-[11px] text-gray-400">
                Security built in, not added on.
              </p>
            </div>
            <div className="flex flex-col items-center text-center group">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 group-hover:bg-[#1b667a] flex items-center justify-center mb-2.5 transition-colors duration-300">
                <Icon
                  icon="mdi:chart-line"
                  className="w-4.5 h-4.5 text-[#1b667a] group-hover:text-white transition-colors"
                />
              </div>
              <h4 className="text-[13px] font-bold text-gray-900 mb-0.5">
                Scalable Solutions
              </h4>
              <p className="text-[11px] text-gray-400">
                Built to grow with your business.
              </p>
            </div>
            <div className="flex flex-col items-center text-center group">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 group-hover:bg-[#1b667a] flex items-center justify-center mb-2.5 transition-colors duration-300">
                <Icon
                  icon="mdi:server"
                  className="w-4.5 h-4.5 text-[#1b667a] group-hover:text-white transition-colors"
                />
              </div>
              <h4 className="text-[13px] font-bold text-gray-900 mb-0.5">
                High Availability
              </h4>
              <p className="text-[11px] text-gray-400">
                Reliable uptime backed by clear service targets.
              </p>
            </div>
            <div className="flex flex-col items-center text-center group">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 group-hover:bg-[#1b667a] flex items-center justify-center mb-2.5 transition-colors duration-300">
                <Icon
                  icon="mdi:rocket-launch"
                  className="w-4.5 h-4.5 text-[#1b667a] group-hover:text-white transition-colors"
                />
              </div>
              <h4 className="text-[13px] font-bold text-gray-900 mb-0.5">
                Future Ready
              </h4>
              <p className="text-[11px] text-gray-400">
                Technology that evolves with your needs.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
